// taxEngine.js — 2024/25 Tax Rates for 12 Countries
// Covers: US, UK, IN, CA, AU, DE, FR, SG, AE, JP, NL, SE

const PERIODS = { annual: 1, monthly: 12, biweekly: 26, weekly: 52, daily: 260, hourly: 2080 };

function toAnnual(amount, period) {
  return amount * (PERIODS[period] || 1);
}

// ─── UNITED STATES ────────────────────────────────────────────────────────────
const US_FEDERAL_BRACKETS = {
  single: [
    [0, 11600, 0.10], [11600, 47150, 0.12], [47150, 100525, 0.22],
    [100525, 191950, 0.24], [191950, 243725, 0.32], [243725, 609350, 0.35], [609350, Infinity, 0.37]
  ],
  married: [
    [0, 23200, 0.10], [23200, 94300, 0.12], [94300, 201050, 0.22],
    [201050, 383900, 0.24], [383900, 487450, 0.32], [487450, 731200, 0.35], [731200, Infinity, 0.37]
  ],
  hoh: [
    [0, 16550, 0.10], [16550, 63100, 0.12], [63100, 100500, 0.22],
    [100500, 191950, 0.24], [191950, 243700, 0.32], [243700, 609350, 0.35], [609350, Infinity, 0.37]
  ]
};

const US_STATE_RATES = {
  AL: 0.05, AK: 0, AZ: 0.025, AR: 0.047, CA: 0.093, CO: 0.044, CT: 0.065,
  DE: 0.066, FL: 0, GA: 0.055, HI: 0.11, ID: 0.058, IL: 0.0495, IN: 0.0305,
  IA: 0.057, KS: 0.057, KY: 0.045, LA: 0.0425, ME: 0.075, MD: 0.0575,
  MA: 0.05, MI: 0.0425, MN: 0.0985, MS: 0.05, MO: 0.054, MT: 0.069,
  NE: 0.0664, NV: 0, NH: 0, NJ: 0.1075, NM: 0.059, NY: 0.109, NC: 0.0475,
  ND: 0.029, OH: 0.035, OK: 0.0475, OR: 0.099, PA: 0.0307, RI: 0.0599,
  SC: 0.07, SD: 0, TN: 0, TX: 0, UT: 0.0465, VT: 0.0875, VA: 0.0575,
  WA: 0, WV: 0.065, WI: 0.0765, WY: 0, DC: 0.1075, X: 0
};

function calcUS(gross, region = 'X', status = 'single') {
  const st = status === 'married' ? 'married' : status === 'hoh' ? 'hoh' : 'single';
  const stdDeductions = { single: 14600, married: 29200, hoh: 21900 };
  const taxable = Math.max(0, gross - stdDeductions[st]);

  let federal = 0;
  const brackets = US_FEDERAL_BRACKETS[st];
  for (const [lo, hi, rate] of brackets) {
    if (taxable <= lo) break;
    federal += (Math.min(taxable, hi) - lo) * rate;
  }

  const stateRate = US_STATE_RATES[region] || 0;
  const stateTax = taxable * stateRate;

  const ss = Math.min(gross, 168600) * 0.062;
  const medicare = gross <= 200000 ? gross * 0.0145 : 200000 * 0.0145 + (gross - 200000) * 0.0235;
  const totalTax = federal + stateTax + ss + medicare;

  return {
    gross, net: gross - totalTax, totalTax,
    breakdown: [
      { label: 'Federal Income Tax', amount: federal },
      { label: `State Tax (${region || 'None'})`, amount: stateTax },
      { label: 'Social Security', amount: ss },
      { label: 'Medicare', amount: medicare }
    ]
  };
}

// ─── UNITED KINGDOM ───────────────────────────────────────────────────────────
function calcUK(gross, region = 'ENG', _status = 'single') {
  const isScotland = region === 'SCT';
  let incomeTax = 0;

  // Personal allowance taper >£100k
  const rawAllowance = 12570;
  const allowance = gross > 100000 ? Math.max(0, rawAllowance - (gross - 100000) / 2) : rawAllowance;
  const taxable = Math.max(0, gross - allowance);

  if (isScotland) {
    const sBrackets = [[0,2306,0.19],[2306,13991,0.20],[13991,31092,0.21],[31092,62430,0.42],[62430,125140,0.45],[125140,Infinity,0.48]];
    for (const [lo, hi, rate] of sBrackets) {
      if (taxable <= lo) break;
      incomeTax += (Math.min(taxable, hi) - lo) * rate;
    }
  } else {
    const eBrackets = [[0,37700,0.20],[37700,125140,0.40],[125140,Infinity,0.45]];
    for (const [lo, hi, rate] of eBrackets) {
      if (taxable <= lo) break;
      incomeTax += (Math.min(taxable, hi) - lo) * rate;
    }
  }

  // National Insurance Class 1
  let ni = 0;
  if (gross > 12570) {
    const niBase = Math.min(gross, 50270) - 12570;
    ni += Math.max(0, niBase) * 0.08;
    if (gross > 50270) ni += (gross - 50270) * 0.02;
  }

  const totalTax = incomeTax + ni;
  return {
    gross, net: gross - totalTax, totalTax,
    breakdown: [
      { label: 'Income Tax', amount: incomeTax },
      { label: 'National Insurance', amount: ni }
    ]
  };
}

// ─── INDIA ────────────────────────────────────────────────────────────────────
function calcIN(gross, _region = null, status = 'new') {
  const isOld = status === 'old';

  let incomeTax = 0;
  if (isOld) {
    const deduction = 50000; // Standard deduction
    const taxable = Math.max(0, gross - deduction - 150000); // 80C basic
    const brackets = [[0,250000,0],[250000,500000,0.05],[500000,1000000,0.20],[1000000,Infinity,0.30]];
    for (const [lo, hi, rate] of brackets) {
      if (taxable <= lo) break;
      incomeTax += (Math.min(taxable, hi) - lo) * rate;
    }
    if (gross <= 500000) incomeTax = Math.max(0, incomeTax - 12500); // 87A rebate
  } else {
    // New regime 2024
    const taxable = Math.max(0, gross - 75000); // Standard deduction
    const brackets = [[0,300000,0],[300000,700000,0.05],[700000,1000000,0.10],[1000000,1200000,0.15],[1200000,1500000,0.20],[1500000,Infinity,0.30]];
    for (const [lo, hi, rate] of brackets) {
      if (taxable <= lo) break;
      incomeTax += (Math.min(taxable, hi) - lo) * rate;
    }
    if (gross <= 700000) incomeTax = 0; // 87A rebate new regime
  }

  const surcharge = gross > 5000000 ? incomeTax * 0.10 : gross > 10000000 ? incomeTax * 0.15 : 0;
  const cess = (incomeTax + surcharge) * 0.04;
  const pf = Math.min(gross * 0.12, 21600);
  const professionalTax = 2400;
  const totalTax = incomeTax + surcharge + cess + pf + professionalTax;

  return {
    gross, net: gross - totalTax, totalTax,
    breakdown: [
      { label: 'Income Tax', amount: incomeTax + surcharge },
      { label: 'Health & Education Cess', amount: cess },
      { label: 'Provident Fund (PF)', amount: pf },
      { label: 'Professional Tax', amount: professionalTax }
    ]
  };
}

// ─── CANADA ───────────────────────────────────────────────────────────────────
const CA_FEDERAL_BRACKETS = [
  [0, 55867, 0.15], [55867, 111733, 0.205], [111733, 154906, 0.26],
  [154906, 220000, 0.29], [220000, Infinity, 0.33]
];

const CA_PROVINCE_RATES = {
  AB: { brackets: [[0,148269,0.10],[148269,177922,0.12],[177922,237230,0.13],[237230,355845,0.14],[355845,Infinity,0.15]] },
  BC: { brackets: [[0,45654,0.0506],[45654,91310,0.077],[91310,104835,0.105],[104835,127299,0.1229],[127299,172602,0.147],[172602,240716,0.168],[240716,Infinity,0.205]] },
  ON: { brackets: [[0,51446,0.0505],[51446,102894,0.0915],[102894,150000,0.1116],[150000,220000,0.1216],[220000,Infinity,0.1316]] },
  QC: { brackets: [[0,51780,0.14],[51780,103545,0.19],[103545,126000,0.24],[126000,Infinity,0.2575]] },
  MB: { brackets: [[0,36842,0.108],[36842,79625,0.1275],[79625,Infinity,0.174]] },
  SK: { brackets: [[0,49720,0.105],[49720,142058,0.125],[142058,Infinity,0.145]] },
  NS: { brackets: [[0,29590,0.0879],[29590,59180,0.1495],[59180,93000,0.1667],[93000,150000,0.175],[150000,Infinity,0.21]] },
  NB: { brackets: [[0,47715,0.094],[47715,95431,0.14],[95431,176756,0.16],[176756,Infinity,0.195]] },
  X: { brackets: [[0,Infinity,0.12]] }
};

function calcCA(gross, region = 'ON', _status = 'single') {
  const bpa = 15705;
  const taxable = Math.max(0, gross - bpa);

  let federal = 0;
  for (const [lo, hi, rate] of CA_FEDERAL_BRACKETS) {
    if (taxable <= lo) break;
    federal += (Math.min(taxable, hi) - lo) * rate;
  }
  federal = Math.max(0, federal - bpa * 0.15);

  const prov = CA_PROVINCE_RATES[region] || CA_PROVINCE_RATES['X'];
  let provincial = 0;
  for (const [lo, hi, rate] of prov.brackets) {
    if (gross <= lo) break;
    provincial += (Math.min(gross, hi) - lo) * rate;
  }

  const cpp = Math.min(Math.max(0, gross - 3500), 68500) * 0.0595;
  const ei = Math.min(gross, 63200) * 0.0166;
  const totalTax = federal + provincial + cpp + ei;

  return {
    gross, net: gross - totalTax, totalTax,
    breakdown: [
      { label: 'Federal Income Tax', amount: federal },
      { label: `Provincial Tax (${region})`, amount: provincial },
      { label: 'CPP Contributions', amount: cpp },
      { label: 'EI Premiums', amount: ei }
    ]
  };
}

// ─── AUSTRALIA ────────────────────────────────────────────────────────────────
function calcAU(gross, _region = null, status = 'resident') {
  const isResident = status !== 'nonresident';
  let incomeTax = 0;

  if (isResident) {
    const brackets = [[0,18200,0],[18200,45000,0.19],[45000,120000,0.325],[120000,180000,0.37],[180000,Infinity,0.45]];
    for (const [lo, hi, rate] of brackets) {
      if (gross <= lo) break;
      incomeTax += (Math.min(gross, hi) - lo) * rate;
    }
    // LITO
    let lito = 0;
    if (gross <= 37500) lito = 700;
    else if (gross <= 45000) lito = 700 - (gross - 37500) * 0.05;
    else if (gross <= 66667) lito = 325 - (gross - 45000) * 0.015;
    incomeTax = Math.max(0, incomeTax - lito);
  } else {
    const brackets = [[0,45000,0.325],[45000,120000,0.37],[120000,180000,0.45],[180000,Infinity,0.47]];
    for (const [lo, hi, rate] of brackets) {
      if (gross <= lo) break;
      incomeTax += (Math.min(gross, hi) - lo) * rate;
    }
  }

  const medicare = isResident && gross > 26000 ? gross * 0.02 : 0;
  const totalTax = incomeTax + medicare;

  return {
    gross, net: gross - totalTax, totalTax,
    breakdown: [
      { label: 'Income Tax', amount: incomeTax },
      { label: 'Medicare Levy', amount: medicare }
    ]
  };
}

// ─── GERMANY ──────────────────────────────────────────────────────────────────
function calcDE(gross, _region = null, status = 'single') {
  const isMarried = status === 'married';
  const zve = isMarried ? gross / 2 : gross;

  let incomeTax = 0;
  if (zve <= 11604) {
    incomeTax = 0;
  } else if (zve <= 17005) {
    const y = (zve - 11604) / 10000;
    incomeTax = (979.18 * y + 1400) * y;
  } else if (zve <= 66760) {
    const z = (zve - 17005) / 10000;
    incomeTax = (192.59 * z + 2397) * z + 966.53;
  } else if (zve <= 277825) {
    incomeTax = 0.42 * zve - 10602.13;
  } else {
    incomeTax = 0.45 * zve - 18936.88;
  }
  if (isMarried) incomeTax *= 2;

  const soli = incomeTax > 18130 ? incomeTax * 0.055 : 0;
  const pensionInsurance = Math.min(gross, 90600) * 0.093;
  const healthInsurance = Math.min(gross, 62100) * 0.073;
  const unemploymentInsurance = Math.min(gross, 90600) * 0.013;
  const nursingInsurance = Math.min(gross, 62100) * 0.017;

  const totalTax = incomeTax + soli + pensionInsurance + healthInsurance + unemploymentInsurance + nursingInsurance;

  return {
    gross, net: gross - totalTax, totalTax,
    breakdown: [
      { label: 'Income Tax (Lohnsteuer)', amount: incomeTax },
      { label: 'Solidarity Surcharge', amount: soli },
      { label: 'Pension Insurance', amount: pensionInsurance },
      { label: 'Health Insurance', amount: healthInsurance },
      { label: 'Unemployment Insurance', amount: unemploymentInsurance },
      { label: 'Nursing Insurance', amount: nursingInsurance }
    ]
  };
}

// ─── FRANCE ───────────────────────────────────────────────────────────────────
function calcFR(gross, _region = null, status = 'single') {
  const quotient = status === 'married' ? 2 : status === 'family' ? 2.5 : 1;
  const taxable = gross * 0.9; // 10% professional expenses deduction
  const perPart = taxable / quotient;

  const brackets = [[0,11294,0],[11294,28797,0.11],[28797,82341,0.30],[82341,177106,0.41],[177106,Infinity,0.45]];
  let taxPerPart = 0;
  for (const [lo, hi, rate] of brackets) {
    if (perPart <= lo) break;
    taxPerPart += (Math.min(perPart, hi) - lo) * rate;
  }
  const incomeTax = taxPerPart * quotient;

  const socialContributions = gross * 0.2282;

  const totalTax = incomeTax + socialContributions;
  return {
    gross, net: gross - totalTax, totalTax,
    breakdown: [
      { label: 'Income Tax (IR)', amount: incomeTax },
      { label: 'Social Contributions', amount: socialContributions }
    ]
  };
}

// ─── SINGAPORE ────────────────────────────────────────────────────────────────
function calcSG(gross, _region = null, status = 'resident') {
  const isResident = status !== 'nonresident';
  let incomeTax = 0;

  if (isResident) {
    const earnedIncomeRelief = gross > 55000 ? 1000 : gross > 22000 ? 3000 : gross > 6000 ? 5000 : 0;
    const taxable = Math.max(0, gross - earnedIncomeRelief);
    const brackets = [[0,20000,0],[20000,30000,0.02],[30000,40000,0.035],[40000,80000,0.07],[80000,120000,0.115],[120000,160000,0.15],[160000,200000,0.18],[200000,240000,0.19],[240000,280000,0.195],[280000,320000,0.20],[320000,500000,0.22],[500000,1000000,0.23],[1000000,Infinity,0.24]];
    for (const [lo, hi, rate] of brackets) {
      if (taxable <= lo) break;
      incomeTax += (Math.min(taxable, hi) - lo) * rate;
    }
  } else {
    incomeTax = gross * 0.22;
  }

  const cpf = isResident ? Math.min(gross, 102000) * 0.20 : 0;
  const totalTax = incomeTax + cpf;

  return {
    gross, net: gross - totalTax, totalTax,
    breakdown: [
      { label: 'Income Tax', amount: incomeTax },
      { label: 'CPF (Employee)', amount: cpf }
    ]
  };
}

// ─── UAE ──────────────────────────────────────────────────────────────────────
function calcAE(gross) {
  return {
    gross, net: gross, totalTax: 0,
    breakdown: [{ label: 'Income Tax', amount: 0 }]
  };
}

// ─── JAPAN ────────────────────────────────────────────────────────────────────
function calcJP(gross, _region = null, _status = 'single') {
  const employmentDeduction = gross <= 1625000 ? 650000
    : gross <= 1800000 ? gross * 0.4
    : gross <= 3600000 ? gross * 0.3 + 180000
    : gross <= 6600000 ? gross * 0.2 + 540000
    : gross <= 8500000 ? gross * 0.1 + 1200000
    : 1950000;

  const basicDeduction = 480000;
  const taxable = Math.max(0, gross - employmentDeduction - basicDeduction);

  const brackets = [[0,1950000,0.05],[1950000,3300000,0.10],[3300000,6950000,0.20],[6950000,9000000,0.23],[9000000,18000000,0.33],[18000000,40000000,0.40],[40000000,Infinity,0.45]];
  let nationalTax = 0;
  for (const [lo, hi, rate] of brackets) {
    if (taxable <= lo) break;
    nationalTax += (Math.min(taxable, hi) - lo) * rate;
  }
  const surtax = nationalTax * 0.021;
  const localTax = taxable * 0.10;
  const socialInsurance = gross * 0.1502;
  const totalTax = nationalTax + surtax + localTax + socialInsurance;

  return {
    gross, net: gross - totalTax, totalTax,
    breakdown: [
      { label: 'National Income Tax', amount: nationalTax + surtax },
      { label: 'Local Inhabitant Tax', amount: localTax },
      { label: 'Social Insurance', amount: socialInsurance }
    ]
  };
}

// ─── NETHERLANDS ──────────────────────────────────────────────────────────────
function calcNL(gross, _region = null, _status = 'single') {
  let box1 = 0;
  const brackets = [[0,75518,0.3693],[75518,Infinity,0.4950]];
  for (const [lo, hi, rate] of brackets) {
    if (gross <= lo) break;
    box1 += (Math.min(gross, hi) - lo) * rate;
  }

  // General tax credit
  let generalCredit = 0;
  if (gross <= 24813) generalCredit = 3374;
  else if (gross <= 75518) generalCredit = Math.max(0, 3374 - (gross - 24813) * 0.0663);

  // Labour discount
  let labourDiscount = 0;
  if (gross <= 11491) labourDiscount = gross * 0.08053;
  else if (gross <= 23201) labourDiscount = 925 + (gross - 11491) * 0.02955;
  else if (gross <= 37691) labourDiscount = 1272 + (gross - 23201) * 0.02987;
  else if (gross <= 128693) labourDiscount = Math.max(0, 5052 - (gross - 37691) * 0.06510);

  const incomeTax = Math.max(0, box1 - generalCredit - labourDiscount);
  const socialContributions = Math.min(gross, 38098) * 0.2765;

  const totalTax = incomeTax + socialContributions;
  return {
    gross, net: gross - totalTax, totalTax,
    breakdown: [
      { label: 'Income Tax (Box 1)', amount: incomeTax },
      { label: 'Social Contributions', amount: socialContributions }
    ]
  };
}

// ─── SWEDEN ───────────────────────────────────────────────────────────────────
function calcSE(gross, _region = null, _status = 'single') {
  const municipal = gross * 0.32;
  const stateTax = gross > 598500 ? (gross - 598500) * 0.20 : 0;
  const socialFee = gross * 0.07;
  const totalTax = municipal + stateTax + socialFee;

  return {
    gross, net: gross - totalTax, totalTax,
    breakdown: [
      { label: 'Municipal Tax (32%)', amount: municipal },
      { label: 'State Income Tax', amount: stateTax },
      { label: 'Social Security Fee', amount: socialFee }
    ]
  };
}

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────
const CALCULATORS = { US: calcUS, UK: calcUK, IN: calcIN, CA: calcCA, AU: calcAU, DE: calcDE, FR: calcFR, SG: calcSG, AE: calcAE, JP: calcJP, NL: calcNL, SE: calcSE };

export function computeTax(country, salary, period = 'annual', region = null, status = 'single') {
  if (!salary || salary <= 0) return null;
  const annual = toAnnual(Number(salary), period);
  const calc = CALCULATORS[country];
  if (!calc) return null;
  const result = calc(annual, region, status);
  if (!result) return null;

  const { gross, net, totalTax, breakdown } = result;
  const effectiveRate = gross > 0 ? (totalTax / gross) * 100 : 0;
  const netPercent = gross > 0 ? (net / gross) * 100 : 0;

  const periodMap = {
    annual: { label: 'Annual', divisor: 1 },
    monthly: { label: 'Monthly', divisor: 12 },
    biweekly: { label: 'Bi-Weekly', divisor: 26 },
    weekly: { label: 'Weekly', divisor: 52 },
    daily: { label: 'Daily', divisor: 260 },
    hourly: { label: 'Hourly', divisor: 2080 }
  };

  const periods = Object.entries(periodMap).map(([key, { label, divisor }]) => ({
    key, label,
    gross: gross / divisor,
    tax: totalTax / divisor,
    net: net / divisor
  }));

  return { gross, net, totalTax, effectiveRate, netPercent, breakdown, periods };
}

export { CALCULATORS, toAnnual };